import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Loader2, Clock, User, FileText, RotateCcw, Check, GitCompare, X } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { Checkbox } from './ui/checkbox';

interface SOWVersionHistoryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sowId: string;
  firmId: string;
  accessToken: string;
  onVersionRestored?: () => void;
}

interface SOWVersion {
  sowId: string;
  version: number;
  data: any;
  createdAt: string;
  createdBy: string;
  changeNote?: string;
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  signed: 'bg-purple-100 text-purple-700',
  active: 'bg-green-100 text-green-700',
};

export function SOWVersionHistory({
  open,
  onOpenChange,
  sowId,
  firmId,
  accessToken,
  onVersionRestored,
}: SOWVersionHistoryProps) {
  const [loading, setLoading] = useState(true);
  const [versions, setVersions] = useState<SOWVersion[]>([]);
  const [currentVersion, setCurrentVersion] = useState<number | null>(null);
  const [restoring, setRestoring] = useState<number | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<SOWVersion | null>(null);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState<number[]>([]);
  const [showComparison, setShowComparison] = useState(false);

  useEffect(() => {
    if (open) {
      loadVersions();
    }
  }, [open, sowId]);

  const loadVersions = async () => {
    try {
      setLoading(true);
      
      // Load current SOW to get current version
      const currentResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/sows/${sowId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (currentResponse.ok) {
        const currentData = await currentResponse.json();
        const version = currentData.sow?.version || 1;
        console.log(`[VERSION HISTORY] Current SOW version: ${version}`, currentData.sow);
        setCurrentVersion(version);
      }

      // DEBUG: Check what versions actually exist in KV store
      const debugResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/sows/${sowId}/debug-versions`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );
      
      if (debugResponse.ok) {
        const debugData = await debugResponse.json();
        console.log(`[VERSION HISTORY DEBUG] KV store data:`, debugData);
      }

      // Load version history
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/sows/${sowId}/versions`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log(`[VERSION HISTORY] Fetched ${data.versions?.length || 0} versions from API:`, data.versions);
        // Filter out any null or invalid versions
        const validVersions = (data.versions || []).filter((v: any) => v && v.version);
        console.log(`[VERSION HISTORY] Displaying ${validVersions.length} valid versions:`, validVersions);
        setVersions(validVersions);
      } else {
        console.error(`[VERSION HISTORY] Failed to load versions, status: ${response.status}`);
        toast.error('Failed to load version history');
      }
    } catch (error) {
      console.error('Error loading versions:', error);
      toast.error('Error loading version history');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (version: number) => {
    if (!confirm(`Are you sure you want to restore to version ${version}? This will create a new version based on the selected version.`)) {
      return;
    }

    try {
      setRestoring(version);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/sows/${sowId}/restore/${version}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            restoredBy: 'current-user', // TODO: Get from context
          }),
        }
      );

      if (response.ok) {
        toast.success(`Successfully restored to version ${version}`);
        loadVersions();
        if (onVersionRestored) {
          onVersionRestored();
        }
      } else {
        toast.error('Failed to restore version');
      }
    } catch (error) {
      console.error('Error restoring version:', error);
      toast.error('Error restoring version');
    } finally {
      setRestoring(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getChangeSummary = (versionData: any) => {
    const changes: string[] = [];
    
    if (versionData.status) {
      changes.push(`Status: ${versionData.status}`);
    }
    
    if (versionData.pricing?.total) {
      changes.push(`Total: $${versionData.pricing.total.toLocaleString()}`);
    }
    
    if (versionData.deliverables?.length) {
      changes.push(`${versionData.deliverables.length} deliverables`);
    }
    
    return changes.join(' • ');
  };

  const toggleCompareMode = () => {
    setCompareMode(!compareMode);
    setSelectedVersions([]);
    setShowComparison(false);
  };

  const handleVersionSelect = (version: number) => {
    if (compareMode) {
      if (selectedVersions.includes(version)) {
        setSelectedVersions(selectedVersions.filter(v => v !== version));
      } else {
        setSelectedVersions([...selectedVersions, version]);
      }
    } else {
      const versionData = versions.find(v => v.version === version);
      if (versionData) {
        setSelectedVersion(versionData);
      }
    }
  };

  const handleCompare = () => {
    if (selectedVersions.length === 2) {
      setShowComparison(true);
    } else {
      toast.error('Please select exactly two versions to compare');
    }
  };

  const getComparisonData = () => {
    // Sort selected versions so the older version is first, newer version is second
    const sortedVersions = [...selectedVersions].sort((a, b) => a - b);
    const [version1, version2] = sortedVersions.map(v => versions.find(v2 => v2.version === v));
    if (!version1 || !version2) return null;

    const changes: { field: string, label: string, version1Value: any, version2Value: any, hasChanged: boolean }[] = [];

    // Compare key fields
    const fieldsToCompare = [
      { key: 'name', label: 'Project Name' },
      { key: 'projectTitle', label: 'Project Title' },
      { key: 'clientName', label: 'Client Name' },
      { key: 'status', label: 'Status' },
      { key: 'scope', label: 'Scope of Work' },
      { key: 'description', label: 'Description' },
    ];

    fieldsToCompare.forEach(({ key, label }) => {
      const value1 = (version1 as any)[key];
      const value2 = (version2 as any)[key];
      const hasChanged = value1 !== value2;
      
      if (value1 !== undefined || value2 !== undefined) {
        changes.push({
          field: key,
          label,
          version1Value: value1,
          version2Value: value2,
          hasChanged,
        });
      }
    });

    // Compare deliverables
    const deliverables1 = version1.deliverables || [];
    const deliverables2 = version2.deliverables || [];
    if (deliverables1.length > 0 || deliverables2.length > 0) {
      changes.push({
        field: 'deliverables',
        label: 'Deliverables',
        version1Value: deliverables1,
        version2Value: deliverables2,
        hasChanged: JSON.stringify(deliverables1) !== JSON.stringify(deliverables2),
      });
    }

    // Compare pricing
    const pricing1 = version1.pricing || {};
    const pricing2 = version2.pricing || {};
    const totalPrice1 = pricing1.total || pricing1.amount || pricing1.fixedPrice || 0;
    const totalPrice2 = pricing2.total || pricing2.amount || pricing2.fixedPrice || 0;
    
    if (totalPrice1 !== totalPrice2 || pricing1.model !== pricing2.model) {
      changes.push({
        field: 'pricing',
        label: 'Pricing',
        version1Value: { model: pricing1.model, total: totalPrice1 },
        version2Value: { model: pricing2.model, total: totalPrice2 },
        hasChanged: true,
      });
    }

    // Compare assumptions
    const assumptions1 = version1.assumptions || [];
    const assumptions2 = version2.assumptions || [];
    if (assumptions1.length > 0 || assumptions2.length > 0) {
      changes.push({
        field: 'assumptions',
        label: 'Assumptions',
        version1Value: assumptions1,
        version2Value: assumptions2,
        hasChanged: JSON.stringify(assumptions1) !== JSON.stringify(assumptions2),
      });
    }

    // Compare exclusions
    const exclusions1 = version1.exclusions || [];
    const exclusions2 = version2.exclusions || [];
    if (exclusions1.length > 0 || exclusions2.length > 0) {
      changes.push({
        field: 'exclusions',
        label: 'Exclusions',
        version1Value: exclusions1,
        version2Value: exclusions2,
        hasChanged: JSON.stringify(exclusions1) !== JSON.stringify(exclusions2),
      });
    }

    return changes;
  };

  const renderComparisonValue = (value: any, field: string) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return <p className="text-sm text-gray-400 italic">None</p>;
    }

    if (field === 'deliverables' || field === 'assumptions' || field === 'exclusions') {
      return (
        <ul className="list-disc list-inside text-sm space-y-1">
          {value.map((item: any, i: number) => (
            <li key={i}>
              {typeof item === 'string' ? item : (item.name || item.description || 'Unnamed item')}
            </li>
          ))}
        </ul>
      );
    } else if (field === 'pricing') {
      return (
        <div className="space-y-2">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Pricing Model</p>
            <p className="text-sm font-medium capitalize">
              {value.model || 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Total Cost</p>
            <p className="font-bold text-green-600">
              ${value.total?.toLocaleString() || '0'}
            </p>
          </div>
        </div>
      );
    } else {
      return (
        <p className="text-sm whitespace-pre-wrap">{value || 'N/A'}</p>
      );
    }
  };

  const getChangeType = (value1: any, value2: any) => {
    const isEmpty1 = !value1 || (Array.isArray(value1) && value1.length === 0);
    const isEmpty2 = !value2 || (Array.isArray(value2) && value2.length === 0);

    if (isEmpty1 && !isEmpty2) return 'added';
    if (!isEmpty1 && isEmpty2) return 'removed';
    if (!isEmpty1 && !isEmpty2 && JSON.stringify(value1) !== JSON.stringify(value2)) return 'modified';
    return 'unchanged';
  };

  const getArrayDiff = (arr1: any[], arr2: any[]) => {
    const stringify = (item: any) => {
      const text = typeof item === 'string' ? item : (item.name || item.description || JSON.stringify(item));
      // Normalize text: trim whitespace, remove extra spaces, convert to lowercase for comparison
      return text.trim().replace(/\s+/g, ' ').toLowerCase();
    };
    
    // Debug logging
    console.log('[ARRAY DIFF] Comparing arrays:');
    console.log('Array 1:', arr1);
    console.log('Array 2:', arr2);
    console.log('Array 1 normalized:', arr1.map(stringify));
    console.log('Array 2 normalized:', arr2.map(stringify));
    
    const set1 = new Map(arr1.map((item, idx) => [stringify(item), { item, idx }]));
    const set2 = new Map(arr2.map((item, idx) => [stringify(item), { item, idx }]));
    
    const added: any[] = [];
    const removed: any[] = [];
    const unchanged: any[] = [];
    
    // Find items in arr1
    arr1.forEach(item => {
      const key = stringify(item);
      if (set2.has(key)) {
        unchanged.push(item);
      } else {
        removed.push(item);
      }
    });
    
    // Find items in arr2 that are new
    arr2.forEach(item => {
      const key = stringify(item);
      if (!set1.has(key)) {
        added.push(item);
      }
    });
    
    console.log('[ARRAY DIFF] Results:', { added: added.length, removed: removed.length, unchanged: unchanged.length });
    
    return { added, removed, unchanged };
  };

  const renderComparisonValueWithDiff = (value1: any, value2: any, field: string) => {
    const changeType = getChangeType(value1, value2);

    if (field === 'deliverables' || field === 'assumptions' || field === 'exclusions') {
      const arr1 = value1 || [];
      const arr2 = value2 || [];
      const { added, removed, unchanged } = getArrayDiff(arr1, arr2);

      return (
        <div className="grid grid-cols-2 gap-4">
          {/* Version 1 (Left) */}
          <div className="space-y-2">
            {unchanged.length > 0 && (
              <ul className="list-disc list-inside text-sm space-y-1">
                {unchanged.map((item: any, i: number) => (
                  <li key={i} className="text-gray-700">
                    {typeof item === 'string' ? item : (item.name || item.description || 'Unnamed item')}
                  </li>
                ))}
              </ul>
            )}
            {removed.length > 0 && (
              <ul className="list-disc list-inside text-sm space-y-1">
                {removed.map((item: any, i: number) => (
                  <li key={i} className="text-red-600 bg-red-50 px-2 py-1 rounded">
                    <span className="line-through">
                      {typeof item === 'string' ? item : (item.name || item.description || 'Unnamed item')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {arr1.length === 0 && (
              <p className="text-sm text-gray-400 italic">None</p>
            )}
          </div>

          {/* Version 2 (Right) */}
          <div className="space-y-2">
            {unchanged.length > 0 && (
              <ul className="list-disc list-inside text-sm space-y-1">
                {unchanged.map((item: any, i: number) => (
                  <li key={i} className="text-gray-700">
                    {typeof item === 'string' ? item : (item.name || item.description || 'Unnamed item')}
                  </li>
                ))}
              </ul>
            )}
            {added.length > 0 && (
              <ul className="list-disc list-inside text-sm space-y-1">
                {added.map((item: any, i: number) => (
                  <li key={i} className="text-green-600 bg-green-50 px-2 py-1 rounded font-medium">
                    {typeof item === 'string' ? item : (item.name || item.description || 'Unnamed item')}
                  </li>
                ))}
              </ul>
            )}
            {arr2.length === 0 && (
              <p className="text-sm text-gray-400 italic">None</p>
            )}
          </div>
        </div>
      );
    } else if (field === 'pricing') {
      const pricing1 = value1 || {};
      const pricing2 = value2 || {};
      const modelChanged = pricing1.model !== pricing2.model;
      const totalChanged = pricing1.total !== pricing2.total;

      return (
        <div className="grid grid-cols-2 gap-4">
          {/* Version 1 */}
          <div className="space-y-2">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Pricing Model</p>
              <p className={`text-sm font-medium capitalize ${modelChanged ? 'text-red-600 line-through' : ''}`}>
                {pricing1.model || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Total Cost</p>
              <p className={`font-bold ${totalChanged ? 'text-red-600 line-through' : 'text-green-600'}`}>
                ${pricing1.total?.toLocaleString() || '0'}
              </p>
            </div>
          </div>

          {/* Version 2 */}
          <div className="space-y-2">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Pricing Model</p>
              <p className={`text-sm font-medium capitalize ${modelChanged ? 'text-green-600 font-semibold' : ''}`}>
                {pricing2.model || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Total Cost</p>
              <p className={`font-bold ${totalChanged ? 'text-green-600' : 'text-green-600'}`}>
                ${pricing2.total?.toLocaleString() || '0'}
              </p>
            </div>
          </div>
        </div>
      );
    } else {
      // Text fields - use line-by-line diff
      const text1 = value1 || '';
      const text2 = value2 || '';

      // If texts are identical, show them simply
      if (text1 === text2) {
        return (
          <div className="p-3 rounded bg-gray-50">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{text1 || 'None'}</p>
          </div>
        );
      }

      // Use line-by-line diff for modified text
      return renderTextWithDiff(text1, text2);
    }
  };

  const getCharacterDiff = (str1: string, str2: string) => {
    // Simple character-level diff using dynamic programming (similar to Levenshtein)
    const len1 = str1.length;
    const len2 = str2.length;
    
    // If strings are identical, return early
    if (str1 === str2) {
      return { type: 'unchanged' as const, segments: [{ text: str1, type: 'unchanged' as const }] };
    }
    
    // Build edit distance matrix
    const matrix: number[][] = Array(len1 + 1).fill(null).map(() => Array(len2 + 1).fill(0));
    
    for (let i = 0; i <= len1; i++) matrix[i][0] = i;
    for (let j = 0; j <= len2; j++) matrix[0][j] = j;
    
    for (let i = 1; i <= len1; i++) {
      for (let j = 1; j <= len2; j++) {
        if (str1[i - 1] === str2[j - 1]) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j] + 1,     // deletion
            matrix[i][j - 1] + 1,     // insertion
            matrix[i - 1][j - 1] + 1  // substitution
          );
        }
      }
    }
    
    // Backtrack to find the diff
    const segments1: Array<{ text: string, type: 'unchanged' | 'removed' }> = [];
    const segments2: Array<{ text: string, type: 'unchanged' | 'added' }> = [];
    
    let i = len1, j = len2;
    let temp1 = '', temp2 = '';
    let lastType: 'unchanged' | 'changed' = 'unchanged';
    
    while (i > 0 || j > 0) {
      if (i > 0 && j > 0 && str1[i - 1] === str2[j - 1]) {
        // Characters match
        if (lastType === 'changed') {
          if (temp1) segments1.unshift({ text: temp1, type: 'removed' });
          if (temp2) segments2.unshift({ text: temp2, type: 'added' });
          temp1 = '';
          temp2 = '';
        }
        temp1 = str1[i - 1] + temp1;
        temp2 = str2[j - 1] + temp2;
        lastType = 'unchanged';
        i--;
        j--;
      } else {
        // Characters differ
        if (lastType === 'unchanged') {
          if (temp1) segments1.unshift({ text: temp1, type: 'unchanged' });
          if (temp2) segments2.unshift({ text: temp2, type: 'unchanged' });
          temp1 = '';
          temp2 = '';
        }
        lastType = 'changed';
        
        // Determine the best path
        const deletesCost = i > 0 ? matrix[i - 1][j] : Infinity;
        const insertCost = j > 0 ? matrix[i][j - 1] : Infinity;
        const substituteCost = (i > 0 && j > 0) ? matrix[i - 1][j - 1] : Infinity;
        
        if (deletesCost <= insertCost && deletesCost <= substituteCost && i > 0) {
          // Deletion
          temp1 = str1[i - 1] + temp1;
          i--;
        } else if (insertCost <= substituteCost && j > 0) {
          // Insertion
          temp2 = str2[j - 1] + temp2;
          j--;
        } else if (i > 0 && j > 0) {
          // Substitution
          temp1 = str1[i - 1] + temp1;
          temp2 = str2[j - 1] + temp2;
          i--;
          j--;
        }
      }
    }
    
    // Push remaining segments
    if (temp1) segments1.unshift({ text: temp1, type: lastType === 'unchanged' ? 'unchanged' : 'removed' });
    if (temp2) segments2.unshift({ text: temp2, type: lastType === 'unchanged' ? 'unchanged' : 'added' });
    
    return { segments1, segments2 };
  };

  const renderTextWithDiff = (text1: string, text2: string) => {
    const lines1 = (text1 || '').split('\n');
    const lines2 = (text2 || '').split('\n');
    
    // Normalize for comparison
    const normalize = (line: string) => line.trim().replace(/\s+/g, ' ').toLowerCase();
    
    // Create maps of normalized lines to find matches
    const lineMap1 = new Map<string, number[]>();
    const lineMap2 = new Map<string, number[]>();
    
    lines1.forEach((line, idx) => {
      const norm = normalize(line);
      if (!lineMap1.has(norm)) lineMap1.set(norm, []);
      lineMap1.get(norm)!.push(idx);
    });
    
    lines2.forEach((line, idx) => {
      const norm = normalize(line);
      if (!lineMap2.has(norm)) lineMap2.set(norm, []);
      lineMap2.get(norm)!.push(idx);
    });
    
    // Build alignment between lines
    const maxLines = Math.max(lines1.length, lines2.length);
    const alignedPairs: Array<{ line1: string | null, line2: string | null, idx1: number, idx2: number }> = [];
    
    let idx1 = 0, idx2 = 0;
    
    while (idx1 < lines1.length || idx2 < lines2.length) {
      const line1 = idx1 < lines1.length ? lines1[idx1] : null;
      const line2 = idx2 < lines2.length ? lines2[idx2] : null;
      
      if (line1 !== null && line2 !== null) {
        const norm1 = normalize(line1);
        const norm2 = normalize(line2);
        
        if (norm1 === norm2) {
          // Lines match
          alignedPairs.push({ line1, line2, idx1, idx2 });
          idx1++;
          idx2++;
        } else {
          // Lines differ - check if either appears later
          const line1AppearsInV2 = lineMap2.has(norm1);
          const line2AppearsInV1 = lineMap1.has(norm2);
          
          if (!line1AppearsInV2 && !line2AppearsInV1) {
            // Both are unique - show as modified
            alignedPairs.push({ line1, line2, idx1, idx2 });
            idx1++;
            idx2++;
          } else if (!line1AppearsInV2) {
            // line1 is removed
            alignedPairs.push({ line1, line2: null, idx1, idx2: -1 });
            idx1++;
          } else {
            // line2 is added
            alignedPairs.push({ line1: null, line2, idx1: -1, idx2 });
            idx2++;
          }
        }
      } else if (line1 !== null) {
        alignedPairs.push({ line1, line2: null, idx1, idx2: -1 });
        idx1++;
      } else if (line2 !== null) {
        alignedPairs.push({ line1: null, line2, idx1: -1, idx2 });
        idx2++;
      }
    }
    
    console.log('[TEXT DIFF] Character-level diff enabled, aligned pairs:', alignedPairs.length);
    
    return (
      <div className="grid grid-cols-2 gap-6">
        {/* Version 1 (Left) */}
        <div className="space-y-0 font-mono text-xs">
          {alignedPairs.map((pair, pairIdx) => {
            const { line1, line2 } = pair;
            
            if (line1 === null) {
              // This line doesn't exist in version 1 (it was added in version 2)
              return <div key={`v1-${pairIdx}`} className="px-2 py-0.5 text-gray-300">&nbsp;</div>;
            }
            
            const norm1 = normalize(line1);
            const norm2 = line2 ? normalize(line2) : '';
            
            if (norm1 === norm2) {
              // Line unchanged
              return (
                <div key={`v1-${pairIdx}`} className="px-2 py-0.5 text-gray-700">
                  {line1 || ' '}
                </div>
              );
            } else if (line2 === null) {
              // Line removed entirely
              return (
                <div key={`v1-${pairIdx}`} className="px-2 py-0.5 bg-red-100 text-red-700 border-l-2 border-red-500">
                  <span className="line-through">{line1 || ' '}</span>
                </div>
              );
            } else {
              // Line modified - show character-level diff
              const diff = getCharacterDiff(line1, line2);
              return (
                <div key={`v1-${pairIdx}`} className="px-2 py-0.5 bg-red-50 border-l-2 border-red-300">
                  {diff.segments1?.map((segment, segIdx) => (
                    <span
                      key={segIdx}
                      className={segment.type === 'removed' ? 'bg-red-200 text-red-800 line-through' : 'text-gray-700'}
                    >
                      {segment.text}
                    </span>
                  ))}
                  {!diff.segments1 && <span className="text-gray-700">{line1}</span>}
                </div>
              );
            }
          })}
        </div>
        
        {/* Version 2 (Right) */}
        <div className="space-y-0 font-mono text-xs">
          {alignedPairs.map((pair, pairIdx) => {
            const { line1, line2 } = pair;
            
            if (line2 === null) {
              // This line doesn't exist in version 2 (it was removed)
              return <div key={`v2-${pairIdx}`} className="px-2 py-0.5 text-gray-300">&nbsp;</div>;
            }
            
            const norm1 = line1 ? normalize(line1) : '';
            const norm2 = normalize(line2);
            
            if (norm1 === norm2) {
              // Line unchanged
              return (
                <div key={`v2-${pairIdx}`} className="px-2 py-0.5 text-gray-700">
                  {line2 || ' '}
                </div>
              );
            } else if (line1 === null) {
              // Line added entirely
              return (
                <div key={`v2-${pairIdx}`} className="px-2 py-0.5 bg-green-100 text-green-700 border-l-2 border-green-500 font-semibold">
                  {line2 || ' '}
                </div>
              );
            } else {
              // Line modified - show character-level diff
              const diff = getCharacterDiff(line1, line2);
              return (
                <div key={`v2-${pairIdx}`} className="px-2 py-0.5 bg-green-50 border-l-2 border-green-300">
                  {diff.segments2?.map((segment, segIdx) => (
                    <span
                      key={segIdx}
                      className={segment.type === 'added' ? 'bg-green-200 text-green-800 font-semibold' : 'text-gray-700'}
                    >
                      {segment.text}
                    </span>
                  ))}
                  {!diff.segments2 && <span className="text-gray-700">{line2}</span>}
                </div>
              );
            }
          })}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="size-5 text-blue-600" />
            Version History
          </DialogTitle>
          <DialogDescription>
            View and restore previous versions of this SOW
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="size-8 animate-spin text-gray-400" />
          </div>
        ) : versions.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <FileText className="size-12 mx-auto mb-3 text-gray-400" />
            <p>No version history available</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleCompareMode}
                >
                  {compareMode ? (
                    <>
                      <X className="size-4 mr-1" />
                      Exit Compare Mode
                    </>
                  ) : (
                    <>
                      <GitCompare className="size-4 mr-1" />
                      Compare Versions
                    </>
                  )}
                </Button>
                {compareMode && selectedVersions.length > 0 && (
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                    {selectedVersions.length} selected
                  </Badge>
                )}
              </div>
              {compareMode && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleCompare}
                  disabled={selectedVersions.length !== 2}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  Compare ({selectedVersions.length}/2)
                </Button>
              )}
            </div>
            {versions.map((version) => {
              const isCurrent = version.version === currentVersion;
              const isSelected = selectedVersions.includes(version.version);
              
              return (
                <Card
                  key={version.version}
                  className={`${isCurrent ? 'border-blue-500 bg-blue-50' : ''} ${isSelected ? 'border-purple-500 bg-purple-50' : ''}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold">Version {version.version}</h4>
                          {isCurrent && (
                            <Badge variant="default" className="bg-blue-600">
                              <Check className="size-3 mr-1" />
                              Current
                            </Badge>
                          )}
                          {isSelected && compareMode && (
                            <Badge variant="default" className="bg-purple-600">
                              <Check className="size-3 mr-1" />
                              Selected
                            </Badge>
                          )}
                          {version.status && (
                            <Badge className={STATUS_COLORS[version.status]}>
                              {version.status}
                            </Badge>
                          )}
                        </div>
                        
                        {version.changeNote && (
                          <p className="text-sm text-muted-foreground mb-2">
                            {version.changeNote}
                          </p>
                        )}
                        
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Clock className="size-3" />
                            {formatDate(version.versionCreatedAt || version.createdAt)}
                          </div>
                        </div>
                        
                        <div className="mt-2 text-xs text-muted-foreground">
                          {getChangeSummary(version)}
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        {compareMode ? (
                          <Button
                            variant={isSelected ? "default" : "outline"}
                            size="sm"
                            onClick={() => handleVersionSelect(version.version)}
                            className={isSelected ? "bg-purple-600 hover:bg-purple-700" : ""}
                            disabled={!isSelected && selectedVersions.length >= 2}
                          >
                            {isSelected ? (
                              <>
                                <Check className="size-4 mr-1" />
                                Selected
                              </>
                            ) : (
                              <>
                                <FileText className="size-4 mr-1" />
                                Select
                              </>
                            )}
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleVersionSelect(version.version)}
                          >
                            <FileText className="size-4 mr-1" />
                            View
                          </Button>
                        )}
                        {!isCurrent && !compareMode && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRestore(version.version)}
                            disabled={restoring !== null}
                          >
                            {restoring === version.version ? (
                              <Loader2 className="size-4 animate-spin" />
                            ) : (
                              <>
                                <RotateCcw className="size-4 mr-1" />
                                Restore
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Version Details Modal */}
        {selectedVersion && (
          <Dialog open={!!selectedVersion} onOpenChange={() => setSelectedVersion(null)}>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  Version {selectedVersion.version} Details
                </DialogTitle>
                <DialogDescription>
                  {formatDate(selectedVersion.createdAt)}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                {(selectedVersion.projectTitle || selectedVersion.name) && (
                  <div>
                    <h4 className="font-semibold mb-2">Project Title</h4>
                    <p className="text-sm">{selectedVersion.projectTitle || selectedVersion.name}</p>
                  </div>
                )}
                
                {selectedVersion.clientName && (
                  <div>
                    <h4 className="font-semibold mb-2">Client Name</h4>
                    <p className="text-sm">{selectedVersion.clientName}</p>
                  </div>
                )}
                
                {(selectedVersion.scope || selectedVersion.description) && (
                  <div>
                    <h4 className="font-semibold mb-2">Scope of Work</h4>
                    <p className="text-sm whitespace-pre-wrap">{selectedVersion.scope || selectedVersion.description}</p>
                  </div>
                )}
                
                {selectedVersion.deliverables?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Deliverables</h4>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      {selectedVersion.deliverables.map((d: any, i: number) => (
                        <li key={i}>
                          {typeof d === 'string' ? d : (d.name || d.description || 'Unnamed deliverable')}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold mb-2">Pricing Model</h4>
                    <p className="text-sm capitalize">
                      {selectedVersion.pricing?.model || selectedVersion.pricing?.type || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Total Cost</h4>
                    <p className="text-sm font-bold text-green-600">
                      ${(
                        selectedVersion.pricing?.total || 
                        selectedVersion.pricing?.amount || 
                        selectedVersion.pricing?.fixedPrice || 
                        0
                      ).toLocaleString()}
                    </p>
                  </div>
                </div>
                
                {selectedVersion.assumptions?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Assumptions</h4>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      {selectedVersion.assumptions.map((a: any, i: number) => (
                        <li key={i}>
                          {typeof a === 'string' ? a : (a.description || a.name || 'Unnamed assumption')}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {selectedVersion.exclusions?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Exclusions</h4>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      {selectedVersion.exclusions.map((e: any, i: number) => (
                        <li key={i}>
                          {typeof e === 'string' ? e : (e.description || e.name || 'Unnamed exclusion')}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Version Comparison Modal */}
        {showComparison && (() => {
          const comparisonData = getComparisonData();
          // Sort versions so older is first (left), newer is second (right)
          const sortedVersions = [...selectedVersions].sort((a, b) => a - b);
          const [v1, v2] = sortedVersions;
          
          return (
            <Dialog open={showComparison} onOpenChange={() => setShowComparison(false)}>
              <DialogContent className="!w-[98vw] !max-w-[98vw] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <GitCompare className="size-5 text-purple-600" />
                    Version Comparison
                  </DialogTitle>
                  <DialogDescription>
                    Comparing Version {v1} (older) with Version {v2} (newer)
                  </DialogDescription>
                </DialogHeader>
                
                {comparisonData && comparisonData.length > 0 ? (
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="grid grid-cols-2 gap-4 pb-4 border-b">
                      <div className="text-center">
                        <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">
                          Version {v1} (Older)
                        </Badge>
                      </div>
                      <div className="text-center">
                        <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300">
                          Version {v2} (Newer)
                        </Badge>
                      </div>
                    </div>

                    {/* Comparisons */}
                    {comparisonData.map((change, index) => (
                      <Card
                        key={index}
                        className={change.hasChanged ? 'border-orange-300 bg-orange-50' : 'border-gray-200'}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <h4 className="font-semibold">{change.label}</h4>
                            {change.hasChanged ? (
                              <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300">
                                Changed
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-gray-100 text-gray-700">
                                Unchanged
                              </Badge>
                            )}
                          </div>
                          
                          {renderComparisonValueWithDiff(change.version1Value, change.version2Value, change.field)}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No differences found between these versions</p>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
}